import { useDeleteNodesMutation } from '@/api/nodes/mutations/delete/delete-nodes-mutation'
import { ConfirmationModal } from '@/components/ui/confirmation-modal'
import { useDirectoryContext } from '@/features/drive/entities/directory/directory-context'
import {
    INodeAction,
    IUseActionReturnType,
} from '@/features/drive/functionalities/actions/node/core/shared/utils/node-action-helpers'
import { useNodeItemContext } from '@/features/drive/entities/node/node-item-context'
import { toast } from '@/features/toast/toast'
import { noOp } from '@/utils/functions/no-op'
import { Trash } from 'lucide-react'
import { NodeType } from '@/api/nodes/mutations/directory/create-directory-mutation'
import { Fragment, useMemo, useState } from 'react'
import { useDirectorySelectionContext } from '@/features/drive/functionalities/selection/directory-selection-context'
import { useDirectoryNavigationContext } from '@/features/drive/functionalities/navigation/directory-navigation-context'

export const useNodeDeleteAction = (): IUseActionReturnType => {
    const { node } = useNodeItemContext()

    const {
        state: { selectedCount },
        deletion: { deleteSelected },
    } = useDirectorySelectionContext()

    const { deleteNode, isDeleteOpen, setIsDeleteOpen } = useDeleteNode()

    const action: INodeAction = useMemo(() => {
        return {
            icon: Trash,
            label: 'Delete',
            call: () => setIsDeleteOpen(true),
        }
    }, [setIsDeleteOpen])

    const isFolder = node.type === NodeType.DIRECTORY
    const deleteText = useMemo(() => {
        if (isFolder) return 'folder'
        return node.file?.category.toLowerCase() || 'file'
    }, [node.file?.category, isFolder])

    const nodes = useMemo(() => {
        return (
            <Fragment key="node-delete-action">
                {isDeleteOpen && (
                    <ConfirmationModal
                        open={isDeleteOpen}
                        onOpenChange={setIsDeleteOpen}
                        cancelText={`Keep ${deleteText}`}
                        confirmText={`Delete ${deleteText}`}
                        title="Are you sure?"
                        subtitle="This action cannot be undone."
                        content={
                            isFolder ? (
                                <>
                                    You are about to delete folder:{' '}
                                    <span className="font-semibold text-gray-900">{node.name}</span>. All files and ads
                                    inside it will be permanently removed.
                                </>
                            ) : (
                                <>
                                    You are about to delete {deleteText}:{' '}
                                    <span className="font-semibold text-gray-900">{node.name}</span>
                                </>
                            )
                        }
                        requireConfirmation={isFolder}
                        destructive
                        onConfirm={() => {
                            if (selectedCount > 0) {
                                deleteSelected?.()
                                return
                            }
                            deleteNode?.()
                        }}
                    />
                )}
            </Fragment>
        )
    }, [deleteNode, deleteSelected, deleteText, isDeleteOpen, isFolder, node.name, selectedCount, setIsDeleteOpen])

    return { action, nodes }
}

const useDeleteNode = () => {
    const { node } = useNodeItemContext()

    const [isDeleteOpen, setIsDeleteOpen] = useState(false)

    const { content } = useDirectoryContext()

    const { closePreviewFile, previewFile } = useDirectoryNavigationContext()

    const { mutateAsync: deleteNodesAsync } = useDeleteNodesMutation({
        nodeContentQueryKey: content.nodeContentQueryKey,
        sendToastError: (count) => toast.error(`An error occurred while deleting the ${count > 1 ? 'items' : 'item'}`),
        sendToastSuccess: (count) => toast.success(`Successfully deleted ${count} ${count > 1 ? 'items' : 'item'}`),
    })

    const deleteNodes = useMemo(() => (node.systemNode ? null : deleteNodesAsync), [node.systemNode, deleteNodesAsync])

    const deleteNode = useMemo(() => {
        if (node.systemNode || !deleteNodes) return noOp

        return () => {
            if (previewFile && previewFile.path === node.path) {
                closePreviewFile()
            }

            deleteNodes({ paths: [node.path] })
        }
    }, [node.systemNode, deleteNodes, node.path, previewFile, closePreviewFile])

    const openDeleteDialog = useMemo(() => {
        if (!deleteNode) return null

        return () => setIsDeleteOpen(true)
    }, [deleteNode])

    return {
        isDeleteOpen,
        setIsDeleteOpen,
        deleteNode,
        openDeleteDialog,
    }
}
